import { z } from 'zod';
import { releaseChannelSchema } from './releases.js';

/* Contratos de métricas do backoffice (Epic 28 / ADR-0353). O admin-service agrega via
   HMAC (auth + licensing + billing) e monta o overview; MRR vem do preço persistido no
   billing (não cross-DB). Consumido pelo dashboard 28-15. */

export const metricsOverviewDto = z.object({
  asOf: z.string(),
  mrrUsd: z.number(),
  mrrBrl: z.number(),
  churn30dPct: z.number(),
  signups7d: z.number().int(),
  signups30d: z.number().int(),
  activeUsers24h: z.number().int(),
  activeDevices24h: z.number().int(),
  totalOrgs: z.number().int(),
  blockedOrgs: z.number().int(),
});
export type MetricsOverviewDto = z.infer<typeof metricsOverviewDto>;

export const revenueByPlanDto = z.object({
  asOf: z.string(),
  items: z.array(
    z.object({
      plan: z.string(),
      customerCount: z.number().int(),
      mrrUsd: z.number(),
      mrrBrl: z.number(),
    }),
  ),
});
export type RevenueByPlanDto = z.infer<typeof revenueByPlanDto>;

/* Só `signups` por enquanto: MRR/churn/active timeseries precisam de snapshots diários
   (ADR-0353 §5, follow-up). `(to-from)/grain ≤ 500 pontos` evita window explosivo. */
export const timeseriesQuery = z.object({
  metric: z.enum(['signups']),
  from: z.string().datetime(),
  to: z.string().datetime(),
  grain: z.enum(['day', 'week', 'month']).default('day'),
});
export type TimeseriesQuery = z.infer<typeof timeseriesQuery>;

export const timeseriesDto = z.object({
  metric: z.string(),
  grain: z.string(),
  points: z.array(z.object({ t: z.string(), v: z.number() })),
});
export type TimeseriesDto = z.infer<typeof timeseriesDto>;

/* Métricas de SLA da moderação de packs (TASK-PUB-12 / ADR-0463). Diferente do overview
   (ADR-0353): dado LOCAL do admin (`pack_review_queue`), não cross-service. `timeToDecision`
   = span `submittedAt → reviewedAt` (o que o SLA-48h do ADR-0363 mede); `aboveSlaCount` conta
   pendências + decisões acima do alvo. `slaTargetHours` vem de `@planuze/config` (§4.17). */
export const moderationMetricsDto = z.object({
  asOf: z.string(),
  queueDepth: z.number().int(),
  timeToDecisionP50Hours: z.number(),
  timeToDecisionP95Hours: z.number(),
  approvedCount: z.number().int(),
  rejectedCount: z.number().int(),
  fastpathCount: z.number().int(),
  autoRejectByCapCount: z.number().int(),
  aboveSlaCount: z.number().int(),
  slaTargetHours: z.number().int(),
});
export type ModerationMetricsDto = z.infer<typeof moderationMetricsDto>;

/* Adoção do app por versão (ADR-0820). A atividade NÃO tem canal no heartbeat e, portanto, aparece
   uma única vez por versão. Downloads continuam aninhados por canal; colocá-los na mesma linha
   plana duplicaria usuários/devices quando a mesma semver existe em stable e beta. */
export const downloadsByVersionDto = z.object({
  asOf: z.string().datetime(),
  windowHours: z.number().int().positive(),
  /* Se false, o snapshot preserva os cliques locais, mas os contadores de
     atividade ficam em zero porque o licensing estava indisponível. */
  activityAvailable: z.boolean().default(true),
  items: z.array(
    z.object({
      version: z.string().min(1),
      activeUsers24h: z.number().int().nonnegative(),
      activeDevices24h: z.number().int().nonnegative(),
      downloads: z.array(
        z.object({
          channel: releaseChannelSchema,
          humanDownloadCount: z.number().int().nonnegative(),
          lastHumanDownloadAt: z.string().datetime().nullable(),
        }),
      ),
    }),
  ),
});
export type DownloadsByVersionDto = z.infer<typeof downloadsByVersionDto>;
